import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { ChevronLeft } from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { authErrorMessage, sendPasswordReset, AccountMergeRequiredError } from '../services/auth';
import { useDocumentHead } from '../lib/seo';

type Mode = 'signin' | 'signup';

export default function SignInPage() {
  const navigate = useNavigate();
  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<null | 'email' | 'google'>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [resetStatus, setResetStatus] = useState<string | null>(null);

  useDocumentHead({
    title: 'Sign in · ParkProof',
    description: 'Sign in to ParkProof to save your garage of cars and submit driver verifications.',
    noindex: true,
  });

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!email.trim() || password.length < 6) {
      setErrorMsg('Email and a 6+ character password are required.');
      return;
    }
    setBusy('email');
    try {
      if (mode === 'signup') await signUp(email, password);
      else await signIn(email, password);
      navigate(-1);
    } catch (err) {
      if (err instanceof AccountMergeRequiredError) {
        await handleAccountMerge(err);
        return;
      }
      const code = (err as { code?: string })?.code ?? '';
      if (mode === 'signin' && code === 'auth/user-not-found') {
        setMode('signup');
        setErrorMsg("You don't seem to have an account — let's create one.");
      } else {
        setErrorMsg(authErrorMessage(err));
      }
    } finally {
      setBusy(null);
    }
  };

  const handleGoogle = async () => {
    setErrorMsg(null);
    setBusy('google');
    try {
      await signInWithGoogle();
      navigate(-1);
    } catch (err) {
      if (err instanceof AccountMergeRequiredError) {
        await handleAccountMerge(err);
        return;
      }
      setErrorMsg(authErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  // This session is anonymous and already has a garage, but the email/
  // provider the user just entered belongs to a different, existing
  // account. Ask before abandoning the anon session's data.
  const handleAccountMerge = async (err: AccountMergeRequiredError) => {
    const proceed = window.confirm(
      'You already have an account with that email or provider. Sign in to it? ' +
        "Anything you've added to this guest session (garage, cars) won't carry over."
    );
    if (!proceed) {
      setErrorMsg('Sign-in cancelled.');
      setBusy(null);
      return;
    }
    try {
      await err.retry();
      navigate(-1);
    } catch (retryErr) {
      setErrorMsg(authErrorMessage(retryErr));
    } finally {
      setBusy(null);
    }
  };

  const handleForgotPassword = async () => {
    setResetStatus(null);
    if (!email.trim()) {
      setErrorMsg('Enter your email above, then tap "Forgot password?".');
      return;
    }
    try {
      await sendPasswordReset(email);
      setResetStatus(`A reset link has been sent to ${email.trim()}.`);
    } catch (err) {
      setErrorMsg(authErrorMessage(err));
    }
  };

  return (
    <div className="flex flex-col">
      <div className="px-s4 py-s2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      <form onSubmit={submitEmail} className="px-s5 pb-s7 flex flex-col gap-s5">
        <div>
          <div className="caption text-ink3 mb-s3 font-mono">
            {mode === 'signup' ? 'CREATE ACCOUNT' : 'SIGN IN'}
          </div>
          <h1 className="font-serif text-[36px] leading-[40px] tracking-tight text-ink1">
            {mode === 'signup' ? 'Start your\n' : 'Welcome\n'}
            <span className="font-serif-italic">{mode === 'signup' ? 'garage' : 'back'}</span>
          </h1>
          <p className="mt-2 text-[14px] text-ink2 font-sans leading-snug">
            {mode === 'signup'
              ? "100% free. Email and a password — that's it. No marketing emails."
              : 'Sign in to access your garage and submit reports.'}
          </p>
        </div>

        {/* Google */}
        <button
          type="button"
          onClick={handleGoogle}
          disabled={busy === 'google'}
          className="h-12 rounded-pill border border-ink1 bg-card flex items-center justify-center gap-2.5 text-[14px] font-sans font-medium text-ink1 disabled:opacity-50 transition-opacity hover:opacity-90"
        >
          {busy === 'google' ? (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-ink1 border-t-transparent" />
          ) : (
            <>
              <span className="w-5 h-5 rounded-full bg-white border border-[#DADCE0] flex items-center justify-center">
                <span className="text-[13px] font-bold text-[#4285F4]">G</span>
              </span>
              <span>Continue with Google</span>
            </>
          )}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <span className="flex-1 h-px bg-rule" />
          <span className="text-[10px] font-sans font-medium text-ink3 tracking-wider">OR EMAIL</span>
          <span className="flex-1 h-px bg-rule" />
        </div>

        <div>
          <label className="caption text-ink3 block mb-2">Email</label>
          <div className="bg-card border border-rule rounded-md px-s4 py-3.5">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={!!busy}
              className="w-full bg-transparent text-[15px] font-sans text-ink1 placeholder-ink3 outline-none"
            />
          </div>
        </div>

        <div>
          <label className="caption text-ink3 block mb-2">Password</label>
          <div className="bg-card border border-rule rounded-md px-s4 py-3.5">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              disabled={!!busy}
              className="w-full bg-transparent text-[15px] font-sans text-ink1 placeholder-ink3 outline-none"
            />
          </div>
          {mode === 'signin' ? (
            <button
              type="button"
              onClick={handleForgotPassword}
              className="block ml-auto mt-2 text-[12px] text-ink3 font-sans underline"
            >
              Forgot password?
            </button>
          ) : null}
        </div>

        {errorMsg ? <div className="text-[13px] text-tier-reported font-sans">{errorMsg}</div> : null}
        {resetStatus ? <div className="text-[13px] text-tier-verified font-sans">{resetStatus}</div> : null}

        <Button block type="submit" loading={busy === 'email'}>
          {mode === 'signup' ? 'Create account' : 'Sign in'}
        </Button>

        <button
          type="button"
          onClick={() => {
            setErrorMsg(null);
            setMode(mode === 'signup' ? 'signin' : 'signup');
          }}
          className="text-[13px] text-ink2 font-sans text-center"
        >
          {mode === 'signup' ? 'Already have an account? ' : "Don't have an account? "}
          <span className="underline font-medium">
            {mode === 'signup' ? 'Sign in' : 'Sign up'}
          </span>
        </button>

        <p className="text-[11px] text-ink3 font-sans text-center">
          By signing up you accept our{' '}
          <Link to="/terms" className="underline">terms of service</Link> and{' '}
          <Link to="/privacy" className="underline">privacy policy</Link>.
        </p>
      </form>
    </div>
  );
}
